import { useCallback, useEffect, useRef, useState } from "react";
import type { SessionPollResult } from "./useAppBootstrapFlow";
import type { SoftphoneSipConfig, UserPermissions } from "./useSoftphoneConfig";

// Ao voltar a visível, só bate de novo se a última batida for mais velha que isto: alternar
// de aba a cada segundo não pode virar uma rajada de POSTs.
const MIN_GAP_ON_VISIBLE_MS = 30_000;

export type SessionPollSnapshot = {
  sip: SoftphoneSipConfig | null;
  permissions: UserPermissions | null;
};

export type SessionChangeReason = "unknownSession" | "tenant" | "licence" | "permissions" | "sip";

export interface SessionPollState {
  /** A configuração vigente difere da recebida no check-in (ou a sessão deixou de existir). */
  reloadPending: boolean;
  /** Motivo da detecção (chave i18n `softphoneWorkspace.sessionReason.*`); `null` enquanto não houver. */
  reloadReason: SessionChangeReason | null;
}

function normalizePermissions(permissions: Partial<UserPermissions> | null | undefined): UserPermissions {
  return {
    recordAccess: permissions?.recordAccess ?? true,
    pabxContactsAccess: permissions?.pabxContactsAccess ?? true,
    // Ausente significa "não é admin" — ao contrário das duas acima, cujo default é permissivo.
    // Entra na comparação de propósito: conceder ou retirar a permissão enquanto o app está
    // aberto agenda a recarga, e é assim que a tela de administração aparece (ou desaparece).
    tenantAdmin: permissions?.tenantAdmin === true,
  };
}

function stableJson(value: unknown): string {
  if (value === null || typeof value !== "object") return JSON.stringify(value);
  const record = value as Record<string, unknown>;
  return `{${Object.keys(record)
    .sort()
    .map((key) => `${JSON.stringify(key)}:${stableJson(record[key])}`)
    .join(",")}}`;
}

// A senha não vem no polling (e não muda entre check-ins: só o próprio check-in a atualiza),
// então a comparação é sobre o resto. Backend e app carregam os mesmos campos do schema.
function sipWithoutPassword(sip: SoftphoneSipConfig | null): Record<string, unknown> | null {
  if (!sip) return null;
  const { password: _password, ...rest } = sip;
  return rest;
}

/**
 * Decide se a resposta do polling significa "o que este documento carregou já não vale".
 * Devolve o motivo, ou `null` se nada mudou.
 */
export function detectSessionChange(snapshot: SessionPollSnapshot, result: SessionPollResult): SessionChangeReason | null {
  if (!result.sessionKnown) return "unknownSession";
  if (result.tenantStatus !== "active") return "tenant";
  if (!result.licenced) return "licence";
  if (stableJson(normalizePermissions(snapshot.permissions)) !== stableJson(normalizePermissions(result.permissions))) {
    return "permissions";
  }
  if (stableJson(sipWithoutPassword(snapshot.sip)) !== stableJson(result.sipUserConfig)) {
    return "sip";
  }
  return null;
}

/**
 * Heartbeat da sessão da aplicação: `POST /sessionPoll` a cada `intervalSeconds` (valor que o
 * backend devolveu no check-in), enquanto o documento viver — oculto ou suspenso também, porque
 * é a batida que mantém a sessão viva no backend; oculto, o Chromium atrasa o timer em até um
 * minuto, e o TTL do backend (3 batidas + 60 s) já conta com isso. Por isso o agendamento é por
 * timestamp: um timer atrasado não empurra os seguintes.
 *
 * Ao voltar a visível bate na hora (cobre o retorno da suspensão sem tocar no caminho de
 * resume: o hook escreve só o próprio estado), respeitando um intervalo mínimo.
 *
 * A resposta é comparada com o que o check-in entregou (`snapshot`). Qualquer diferença —
 * licença, permissões, SIP, status do tenant, sessão desconhecida — vira `reloadPending`, e
 * fica até o reload; quem recarrega é o SoftphoneWorkspace, só com o softphone ocioso. Nada aqui
 * escreve `softphoneConfig`/`registrationStatus`: um reload é a única forma de aplicar a mudança
 * sem deixar o documento num estado que nunca passou pelo check-in.
 *
 * Falhas de rede são `console.debug`: `console.error` viraria POST na captura remota.
 */
export function useSessionPoll({
  enabled,
  intervalSeconds,
  snapshot,
  poll,
  onChangeDetected,
}: {
  enabled: boolean;
  intervalSeconds: number | null;
  snapshot: SessionPollSnapshot;
  poll: () => Promise<SessionPollResult>;
  onChangeDetected?: (reason: SessionChangeReason) => void;
}): SessionPollState {
  const [state, setState] = useState<SessionPollState>({ reloadPending: false, reloadReason: null });
  const inFlightRef = useRef(false);
  const lastPollAtRef = useRef(0);
  const detectedRef = useRef(false);
  const pollRef = useRef(poll);
  pollRef.current = poll;
  const snapshotRef = useRef(snapshot);
  snapshotRef.current = snapshot;
  const callbackRef = useRef(onChangeDetected);
  callbackRef.current = onChangeDetected;

  // Continua batendo depois de detectar: uma aba em chamada pode levar muito tempo até
  // recarregar, e a sessão dela não pode expirar no backend nesse meio-tempo.
  const runPoll = useCallback(async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    lastPollAtRef.current = Date.now();
    try {
      const result = await pollRef.current();
      if (detectedRef.current) return;
      const reason = detectSessionChange(snapshotRef.current, result);
      if (!reason) return;
      detectedRef.current = true;
      console.log(`[session] recarga pendente: ${reason} tenantStatus=${result.tenantStatus} licenced=${result.licenced} sessionKnown=${result.sessionKnown}`);
      setState({ reloadPending: true, reloadReason: reason });
      callbackRef.current?.(reason);
    } catch (error) {
      console.debug(`[session] poll falhou: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      inFlightRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (!enabled || !intervalSeconds) return;

    const intervalMs = intervalSeconds * 1000;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let disposed = false;
    // O check-in que acabou de acontecer conta como a primeira batida.
    lastPollAtRef.current = Date.now();

    const schedule = () => {
      if (disposed) return;
      const delay = Math.max(0, lastPollAtRef.current + intervalMs - Date.now());
      timer = setTimeout(async () => {
        await runPoll();
        schedule();
      }, delay);
    };

    const onVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      if (Date.now() - lastPollAtRef.current < MIN_GAP_ON_VISIBLE_MS) return;
      void runPoll().then(() => {
        if (timer) clearTimeout(timer);
        schedule();
      });
    };

    console.log(`[session] heartbeat a cada ${intervalSeconds}s`);
    schedule();
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      disposed = true;
      if (timer) clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [enabled, intervalSeconds, runPoll]);

  return state;
}
